import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, CmsCase } from '@/lib/supabase';
import CaseHero from './components/CaseHero';
import CaseOverview from './components/CaseOverview';
import CaseChallenges from './components/CaseChallenges';
import CaseSolutions from './components/CaseSolutions';
import CaseResults from './components/CaseResults';
import CaseTestimonial from './components/CaseTestimonial';
import CaseCTA from './components/CaseCTA';
import DetailNavbar from '../business-detail/components/DetailNavbar';
import Footer from '../home/components/Footer';

export default function CaseDetailPage() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState<CmsCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchCase = async () => {
      setLoading(true);
      try {
        // 支持 id（数字）或 slug 两种路由
        const isNumeric = /^\d+$/.test(id);
        const query = supabase.from('cms_cases').select('*');
        const { data, error } = isNumeric
          ? await query.eq('id', parseInt(id, 10)).maybeSingle()
          : await query.eq('slug', id).maybeSingle();
        if (error) throw error;
        if (!data) { setNotFound(true); return; }
        setCaseData(data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <DetailNavbar />
        <div className="h-[500px] bg-gray-100 animate-pulse"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">案例未找到</h1>
          <a href="/" className="text-teal-600 hover:text-teal-700">返回首页</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DetailNavbar />
      <CaseHero caseData={caseData} />
      <CaseOverview caseData={caseData} />
      {caseData.challenges && (caseData.challenges as unknown[]).length > 0 && (
        <CaseChallenges challenges={caseData.challenges as { title: string; desc: string; icon: string }[]} />
      )}
      {caseData.solutions && (caseData.solutions as unknown[]).length > 0 && (
        <CaseSolutions solutions={caseData.solutions as { title: string; desc: string; icon: string }[]} />
      )}
      {caseData.results && (caseData.results as unknown[]).length > 0 && (
        <CaseResults results={caseData.results as { label: string; value: string; unit: string; icon: string }[]} />
      )}
      {caseData.testimonial && (
        <CaseTestimonial testimonial={caseData.testimonial as { content: string; author: string; position: string }} />
      )}
      <CaseCTA />
      <Footer />
    </div>
  );
}
